import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert } from "@/integrations/supabase/types";
import { validateBookingParams } from "@/utils/validators";
import { logger } from "@/utils/logger";
import { createShooting } from "./shootings";
import { getProductById } from "./products";

export type Booking = Tables<"bookings"> & {
  shooting_id?: string | null;
  meeting_type?: 'shooting' | 'editorial' | null;
};

export type BookingInsert = TablesInsert<"bookings"> & {
  shooting_id?: string | null;
  meeting_type?: 'shooting' | 'editorial' | null;
};

/**
 * Crée une nouvelle réservation dans la base de données
 * 
 * COMPORTEMENT :
 * - Valide les paramètres d'entrée
 * - Insère la réservation dans Supabase
 * - Déclenche automatiquement l'auto-attribution du staff pour les tournages first-timer
 * 
 * AUTO-ATTRIBUTION :
 * - Uniquement pour les tournages first-timer (slug contient "shooting" et "first")
 * - Attribution asynchrone (non bloquante)
 * - Attribue un Director et un PBM disponibles
 * - Marque le customer comme non first_timer
 * 
 * @param booking - Données de la réservation à créer
 * @returns {Promise<{success: boolean, data?: Booking, error?: any}>}
 * 
 * @example
 * const result = await createBooking({
 *   user_id: 'user-uuid',
 *   event_type_slug: 'shootingfirst3',
 *   start_time: '2025-01-15T10:00:00Z',
 *   end_time: '2025-01-15T13:00:00Z',
 *   attendee_email: 'user@example.com',
 *   attendee_name: 'John Doe',
 *   location_id: 'location-uuid'
 * });
 * 
 * if (result.success) {
 *   console.log('Booking created:', result.data);
 * }
 */
export const createBooking = async (booking: BookingInsert) => {
  // Validation des paramètres d'entrée
  const validation = validateBookingParams({
    userId: booking.user_id,
    startTime: booking.start_time,
    endTime: booking.end_time,
    eventTypeSlug: booking.event_type_slug,
  });

  if (!validation.valid) {
    logger.error('Invalid booking parameters', { error: validation.error, booking });
    return { 
      success: false, 
      error: validation.error 
    };
  }

  try {
    let shootingId = booking.shooting_id || null;
    
    if ((booking.meeting_type === 'shooting' || booking.meeting_type === 'editorial') && !shootingId) {
      const shootingDate = new Date(booking.start_time);
      const formattedDate = shootingDate.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      
      let productSlug: string | undefined = undefined;
      if (booking.product_id) {
        const product = await getProductById(booking.product_id);
        productSlug = product?.slug || undefined;
      }
      
      const shootingResult = await createShooting({
        userId: booking.user_id,
        title: `Tournage du ${formattedDate}`,
        productId: booking.product_id || undefined,
        locationId: booking.location_id || undefined,
        plan: productSlug,
      });
      
      if (shootingResult.success && shootingResult.data) {
        shootingId = shootingResult.data.id;
        logger.info('Shooting created for booking', { shootingId, bookingId: booking.id });
      } else {
        logger.error('Failed to create shooting for booking', { error: shootingResult.error, booking });
      }
    }
    
    const bookingToInsert: BookingInsert = {
      ...booking,
      shooting_id: shootingId,
    };
    
    const { data, error } = await supabase
      .from("bookings")
      .insert(bookingToInsert)
      .select()
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    logger.error('Failed to create booking', { error, booking });
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

/**
 * Récupère les réservations depuis la base de données
 * 
 * @param userId - ID optionnel de l'utilisateur pour filtrer ses réservations
 * @returns {Promise<{success: boolean, data?: Booking[], error?: any}>}
 * 
 * @example
 * // Récupérer toutes les réservations
 * const allBookings = await getBookings();
 * 
 * // Récupérer les réservations d'un utilisateur
 * const userBookings = await getBookings('user-uuid');
 */
export const getBookings = async (userId?: string) => {
  try {
    let query = supabase
      .from("bookings")
      .select("*, locations(*)")
      .order("start_time", { ascending: true });

    if (userId) {
      query = query.eq("user_id", userId);
    }

    const { data, error } = await query;

    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    logger.error('Failed to fetch bookings', { error, userId });
    return { success: false, error };
  }
};

/**
 * Met à jour une réservation existante
 * 
 * @param id - ID de la réservation à mettre à jour
 * @param updates - Champs à modifier
 * @returns {Promise<{success: boolean, data?: Booking, error?: any}>}
 * 
 * @example
 * const result = await updateBooking('booking-uuid', {
 *   status: 'cancelled',
 *   metadata: { cancellation_reason: 'User request' }
 * });
 */
export const updateBooking = async (id: string, updates: Partial<Booking>) => {
  try {
    logger.info('Updating booking', { bookingId: id, updates });

    const { data, error } = await supabase
      .from("bookings")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    
    logger.info('Booking updated successfully', { bookingId: id });
    return { success: true, data };
  } catch (error) {
    logger.error('Failed to update booking', { error, bookingId: id });
    return { success: false, error };
  }
};

/**
 * Supprime une réservation de la base de données
 * 
 * ATTENTION : Cette opération est irréversible
 * 
 * @param id - ID de la réservation à supprimer
 * @returns {Promise<{success: boolean, error?: any}>}
 * 
 * @example
 * const result = await deleteBooking('booking-uuid');
 * if (result.success) {
 *   console.log('Booking deleted');
 * }
 */
export const deleteBooking = async (id: string) => {
  try {
    logger.info('Deleting booking', { bookingId: id });

    const { error } = await supabase
      .from("bookings")
      .delete()
      .eq("id", id);

    if (error) throw error;
    
    logger.info('Booking deleted successfully', { bookingId: id });
    return { success: true };
  } catch (error) {
    logger.error('Failed to delete booking', { error, bookingId: id });
    return { success: false, error };
  }
};

/**
 * Récupère une réservation par son ID
 * 
 * @param id - ID de la réservation
 * @param selectFields - Champs optionnels à récupérer (par défaut: tous les champs)
 * @returns {Promise<{success: boolean, data?: Booking | Partial<Booking>, error?: any}>}
 */
export const getBookingById = async (id: string, selectFields?: string) => {
  try {
    let query = supabase
      .from("bookings")
      .select(selectFields || "*")
      .eq("id", id);

    const { data, error } = await query.single();

    if (error) throw error;
    
    return { success: true, data };
  } catch (error) {
    logger.error('Failed to fetch booking by ID', { error, id });
    return { success: false, error };
  }
};

/**
 * Récupère une réservation par son booking_uid (UID Cal.com)
 * 
 * @param bookingUid - UID Cal.com de la réservation
 * @returns {Promise<{success: boolean, data?: Booking, error?: any}>}
 */
export const getBookingByUid = async (bookingUid: string) => {
  try {
    const { data, error } = await supabase
      .from("bookings")
      .select("*")
      .eq("booking_uid", bookingUid)
      .maybeSingle();

    if (error) throw error;
    
    return { success: true, data: data || null };
  } catch (error) {
    logger.error('Failed to fetch booking by UID', { error, bookingUid });
    return { success: false, error };
  }
};

/**
 * Met à jour une réservation par son booking_uid (UID Cal.com)
 * 
 * @param bookingUid - UID Cal.com de la réservation à mettre à jour
 * @param updates - Champs à modifier
 * @returns {Promise<{success: boolean, data?: Booking, error?: any}>}
 */
export const updateBookingByUid = async (bookingUid: string, updates: Partial<Booking>) => {
  try {
    logger.info('Updating booking by UID', { bookingUid, updates });

    const { data, error } = await supabase
      .from("bookings")
      .update(updates)
      .eq("booking_uid", bookingUid)
      .select()
      .single();

    if (error) throw error;
    
    logger.info('Booking updated successfully by UID', { bookingUid });
    return { success: true, data };
  } catch (error) {
    logger.error('Failed to update booking by UID', { error, bookingUid });
    return { success: false, error };
  }
};

/**
 * Supprime une réservation par son booking_uid (UID Cal.com)
 * 
 * ATTENTION : Cette opération est irréversible
 * 
 * @param bookingUid - UID Cal.com de la réservation à supprimer
 * @returns {Promise<{success: boolean, error?: any}>}
 */
export const deleteBookingByUid = async (bookingUid: string) => {
  try {
    logger.info('Deleting booking by UID', { bookingUid });

    const { error } = await supabase
      .from("bookings")
      .delete()
      .eq("booking_uid", bookingUid);

    if (error) throw error;
    
    logger.info('Booking deleted successfully by UID', { bookingUid });
    return { success: true };
  } catch (error) {
    logger.error('Failed to delete booking by UID', { error, bookingUid });
    return { success: false, error };
  }
};

/**
 * Récupère une réservation avec les informations du director par booking_uid
 * 
 * @param bookingUid - UID Cal.com de la réservation
 * @returns {Promise<{success: boolean, data?: any, error?: any}>}
 */
export const getBookingWithDirectorByUid = async (bookingUid: string) => {
  try {
    const { data, error } = await supabase
      .from("bookings")
      .select(`
        director_id,
        team_film_directors (user_id, users!film_directors_user_id_fkey(first_name, last_name, avatar_url))
      `)
      .eq("booking_uid", bookingUid)
      .maybeSingle();

    if (error) throw error;
    
    // Map user data from nested users relation
    const directorData = data?.team_film_directors;
    const result = data ? {
      director_id: data.director_id,
      team_film_directors: directorData ? {
        first_name: (directorData as any).users?.first_name || null,
        last_name: (directorData as any).users?.last_name || null,
        avatar_url: (directorData as any).users?.avatar_url || null
      } : null
    } : null;
    
    return { success: true, data: result };
  } catch (error) {
    logger.error('Failed to fetch booking with director by UID', { error, bookingUid });
    return { success: false, error };
  }
};

/**
 * Récupère une réservation de préparation avec les informations du PBM
 * 
 * @param userId - ID de l'utilisateur
 * @param startTime - Heure de début de la réservation (ISO string)
 * @returns {Promise<{success: boolean, data?: any, error?: any}>}
 */
export const getPreparationBookingWithPBM = async (userId: string, startTime: string) => {
  try {
    const { data, error } = await supabase
      .from("bookings")
      .select(`
        personal_brand_manager_id,
        team_personal_brand_managers (user_id, users!personal_brand_managers_user_id_fkey(first_name, last_name, avatar_url))
      `)
      .eq("user_id", userId)
      .eq("start_time", startTime)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    
    // Map user data from nested users relation
    const pbmData = data?.team_personal_brand_managers;
    const result = data ? {
      personal_brand_manager_id: data.personal_brand_manager_id,
      team_personal_brand_managers: pbmData ? {
        first_name: (pbmData as any).users?.first_name || null,
        last_name: (pbmData as any).users?.last_name || null,
        avatar_url: (pbmData as any).users?.avatar_url || null
      } : null
    } : null;
    
    return { success: true, data: result };
  } catch (error) {
    logger.error('Failed to fetch preparation booking with PBM', { error, userId, startTime });
    return { success: false, error };
  }
};

interface CalendarBookingWithRelations extends Booking {
  team_film_directors?: {
    user_id: string;
    users?: {
      first_name: string | null;
      last_name: string | null;
      avatar_url: string | null;
    } | null;
  } | null;
  team_personal_brand_managers?: {
    user_id: string;
    users?: {
      first_name: string | null;
      last_name: string | null;
      avatar_url: string | null;
    } | null;
  } | null;
  linkedEditorialBooking?: CalendarBookingWithRelations | null;
  linkedShootingBookings?: CalendarBookingWithRelations[] | null;
}

interface UserData {
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

/**
 * Extracts user data from nested team relation structure
 */
const extractUserData = (teamData: CalendarBookingWithRelations['team_film_directors'] | CalendarBookingWithRelations['team_personal_brand_managers']): UserData | null => {
  if (!teamData?.users) return null;
  
  return {
    first_name: teamData.users.first_name || null,
    last_name: teamData.users.last_name || null,
    avatar_url: teamData.users.avatar_url || null
  };
};

const groupBookingsByShootingId = (bookings: CalendarBookingWithRelations[]): Map<string, CalendarBookingWithRelations[]> => {
  const shootingToBookings = new Map<string, CalendarBookingWithRelations[]>();
  
  for (const booking of bookings) {
    if (booking.shooting_id) {
      const existing = shootingToBookings.get(booking.shooting_id) || [];
      existing.push(booking);
      shootingToBookings.set(booking.shooting_id, existing);
    }
  }
  
  return shootingToBookings;
};

const findLinkedBookings = (
  booking: CalendarBookingWithRelations,
  allBookingsForShooting: CalendarBookingWithRelations[]
): {
  linkedEditorialBooking: CalendarBookingWithRelations | null;
  linkedShootingBookings: CalendarBookingWithRelations[] | null;
} => {
  if (booking.meeting_type === 'shooting') {
    const linkedEditorialBooking = allBookingsForShooting.find(
      b => b.meeting_type === 'editorial' && b.id !== booking.id
    ) || null;
    
    return {
      linkedEditorialBooking,
      linkedShootingBookings: null
    };
  }
  
  if (booking.meeting_type === 'editorial') {
    const linkedShootingBookings = allBookingsForShooting.filter(
      b => b.meeting_type === 'shooting'
    );
    
    return {
      linkedEditorialBooking: null,
      linkedShootingBookings: linkedShootingBookings.length > 0 ? linkedShootingBookings : null
    };
  }
  
  return {
    linkedEditorialBooking: null,
    linkedShootingBookings: null
  };
};

/**
 * Récupère les bookings pour le calendrier avec toutes les relations nécessaires
 * 
 * @param userId - ID de l'utilisateur
 * @returns {Promise<{success: boolean, data?: CalendarBookingWithRelations[], error?: any}>}
 */
export const getBookingsForCalendar = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('bookings')
      .select(`
        *,
        locations(*),
        studios(*, locations(*)),
        team_film_directors(user_id, users!film_directors_user_id_fkey(first_name, last_name, avatar_url)),
        products(shooting_duration_minutes, preparation_duration_minutes),
        team_personal_brand_managers(user_id, users!personal_brand_managers_user_id_fkey(first_name, last_name, avatar_url))
      `)
      .eq('user_id', userId)
      .neq('status', 'cancelled')
      .order('start_time', { ascending: true });

    if (error) throw error;
    
    if (!data || data.length === 0) {
      return { success: true, data: [] };
    }
    
    const bookings = data as CalendarBookingWithRelations[];
    const shootingToBookings = groupBookingsByShootingId(bookings);
    
    const mappedData = bookings.map(booking => {
      const directorData = extractUserData(booking.team_film_directors);
      const pbmData = extractUserData(booking.team_personal_brand_managers);
      
      const allBookingsForShooting = booking.shooting_id 
        ? shootingToBookings.get(booking.shooting_id) || []
        : [];
      
      const { linkedEditorialBooking, linkedShootingBookings } = findLinkedBookings(
        booking,
        allBookingsForShooting
      );
      
      return {
        ...booking,
        team_film_directors: directorData,
        team_personal_brand_managers: pbmData,
        linkedEditorialBooking,
        linkedShootingBookings
      };
    });
    
    return { success: true, data: mappedData };
  } catch (error) {
    logger.error('Failed to fetch bookings for calendar', { error, userId });
    return { success: false, error, data: [] };
  }
};

/**
 * Récupère les bookings à venir pour l'administration des studios
 */
export const getUpcomingBookingsForAdmin = async () => {
  try {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from('bookings')
      .select(`
        *,
        locations(id, name, city),
        studios(id, name),
        users!bookings_user_id_fkey(first_name, last_name, email),
        team_film_directors(id, user_id, users!film_directors_user_id_fkey(first_name, last_name))
      `)
      .gte('start_time', now)
      .neq('status', 'cancelled')
      .order('start_time', { ascending: true });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    logger.error('Failed to fetch upcoming bookings for admin', { error });
    return { success: false, error, data: [] };
  }
};

/**
 * Met à jour le studio d'un booking
 */
export const updateBookingStudio = async (bookingId: string, studioId: string | null) => {
  try {
    const { data, error } = await supabase
      .from('bookings')
      .update({ studio_id: studioId })
      .eq('id', bookingId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    logger.error('Failed to update booking studio', { error, bookingId, studioId });
    return { success: false, error };
  }
};

/**
 * Met à jour le réalisateur d'un booking
 */
export const updateBookingDirector = async (bookingId: string, directorId: string | null) => {
  try {
    const { data, error } = await supabase
      .from('bookings')
      .update({ director_id: directorId })
      .eq('id', bookingId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    logger.error('Failed to update booking director', { error, bookingId, directorId });
    return { success: false, error };
  }
};

/**
 * Get the earliest shooting date for a user to calculate publication start date
 */
export const getEarliestShootingDate = async (userId: string): Promise<Date | null> => {
  try {
    const { data, error } = await supabase
      .from('bookings')
      .select('start_time')
      .eq('user_id', userId)
      .not('start_time', 'is', null)
      .order('start_time', { ascending: true })
      .limit(1);

    if (error) throw error;

    if (!data || data.length === 0) {
      return null;
    }

    return new Date(data[0].start_time);
  } catch (error) {
    logger.error('Failed to fetch earliest shooting date', { error, userId });
    return null;
  }
};
